import { Link } from 'react-router-dom';
import { ArrowUpRight, Check, Phone } from 'lucide-react';

const SALES_PHONE = '(253) 453-1754';
const SALES_PHONE_TEL = 'tel:+12534531754';

const EditionCard = ({ edition }) => (
  <div
    className="flex flex-col rounded-2xl border border-slate-200 bg-white p-7 md:p-8 hover:shadow-lg hover:-translate-y-0.5 transition-all"
    data-testid={`edition-card-${edition.id}`}
  >
    {edition.logo && (
      <img src={edition.logo} alt="" aria-hidden className="self-start h-10 w-auto mb-4" />
    )}
    <div className="text-[11.5px] uppercase tracking-[0.2em] font-semibold text-[#3E5C76]">{edition.audience}</div>
    <h3 className="mt-3 font-serif text-2xl md:text-[28px] font-semibold text-[#0B2545] tracking-tight">{edition.name}</h3>
    <p className="mt-3 text-[14.5px] leading-relaxed text-slate-600">{edition.tagline}</p>
    <ul className="mt-6 space-y-3 flex-1">
      {edition.points.map((pt) => (
        <li key={pt} className="flex items-start gap-3">
          <div className="mt-0.5 w-5 h-5 rounded-md bg-[#3E5C76] flex items-center justify-center flex-shrink-0">
            <Check className="w-3 h-3 text-white" />
          </div>
          <span className="text-[14px] text-slate-700 leading-relaxed">{pt}</span>
        </li>
      ))}
    </ul>
    <a
      href={edition.url}
      target="_blank"
      rel="noreferrer"
      className="mt-7 group inline-flex items-center justify-center gap-2 rounded-full bg-[#0B2545] hover:bg-[#13315C] text-white text-[14px] font-medium px-6 py-3 transition-all shadow-sm hover:shadow-md"
      data-testid={`edition-launch-${edition.id}`}
    >
      Launch {edition.name}
      <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
    </a>
  </div>
);

const UpcomingEditionCard = ({ edition }) => (
  <div
    className="flex flex-col rounded-2xl border border-dashed border-slate-300 bg-white/70 p-6 hover:border-slate-400 hover:bg-white transition-all"
    data-testid={`upcoming-edition-card-${edition.id}`}
  >
    <div className="flex items-center justify-between">
      {edition.logo ? (
        <img src={edition.logo} alt="" aria-hidden className="h-9 w-auto" />
      ) : (
        <span />
      )}
      <span className="inline-flex items-center rounded-full bg-[#F1F4F8] border border-slate-200 px-2.5 py-0.5 text-[10.5px] font-semibold uppercase tracking-[0.12em] text-slate-500">
        Coming Soon
      </span>
    </div>
    <div className="mt-3 text-[11px] uppercase tracking-[0.18em] font-semibold text-[#3E5C76]">{edition.audience}</div>
    <h3 className="mt-2 font-serif text-xl font-semibold text-[#0B2545] tracking-tight">{edition.name}</h3>
    <p className="mt-2 text-[13.5px] leading-relaxed text-slate-600 flex-1">{edition.tagline}</p>
    <Link
      to="/#contact"
      className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-medium text-[#3E5C76] hover:text-[#0B2545] transition-colors"
      data-testid={`early-access-${edition.id}`}
    >
      Request early access <ArrowUpRight className="w-3.5 h-3.5" />
    </Link>
  </div>
);

export const EditionsSection = ({ editions, upcomingEditions }) => (
  <section className="relative py-20 bg-[#F1F4F8]" data-testid="editions-section">
    <div className="absolute inset-0 topo-bg opacity-60 pointer-events-none" />
    <div className="relative max-w-6xl mx-auto px-5 sm:px-8">
      <div className="max-w-2xl">
        <div className="text-[12px] uppercase tracking-[0.22em] font-semibold text-[#3E5C76]">Built for your desk</div>
        <h2 className="mt-3 font-serif text-3xl md:text-4xl font-medium tracking-[-0.02em] text-[#0B2545]">
          One underwriting brain. An edition for every desk.
        </h2>
        <p className="mt-4 text-[15.5px] leading-relaxed text-slate-600">
          AXIOM ships in purpose-built editions — the same underwriting intelligence, framed for the
          professional using it. Four are live today, with two more on the way.
        </p>
      </div>
      <div className="mt-12 grid md:grid-cols-2 gap-5">
        {editions.map((e) => (
          <EditionCard key={e.id} edition={e} />
        ))}
      </div>
      {upcomingEditions && (
        <>
          <div className="mt-12 text-[12px] uppercase tracking-[0.22em] font-semibold text-slate-500" data-testid="upcoming-editions-label">
            Coming soon
          </div>
          <div className="mt-5 grid sm:grid-cols-2 gap-4">
            {upcomingEditions.map((e) => (
              <UpcomingEditionCard key={e.id} edition={e} />
            ))}
          </div>
        </>
      )}
      <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-6 py-4">
        <div className="text-[13px] text-slate-500">
          Powered by <span className="font-semibold text-[#0B2545]">Northwest Tech Alliance</span>
        </div>
        <a
          href={SALES_PHONE_TEL}
          className="inline-flex items-center gap-2 text-[13.5px] font-medium text-[#3E5C76] hover:text-[#0B2545] transition-colors"
          data-testid="axiom-sales-phone"
        >
          <Phone className="w-4 h-4" /> Need an account? Call sales at {SALES_PHONE}
        </a>
      </div>
    </div>
  </section>
);
